import { lazy, Suspense, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useMsal } from "@azure/msal-react";
import { Route, Routes } from 'react-router-dom';
import { Notifications } from '@mantine/notifications';
import axios from 'axios';
import qs from 'qs';
import { ThemeProvider } from 'styled-components';
import SecureRoute from './SecureRoute';
import api from '../apis/api';
import UserContext from '../contexts/UserContext';
import GlobalStyle from '../GlobalStyles/GlobalStyles';
import { lightTheme, darkTheme } from '../GlobalStyles/theme';
import { requestForToken } from './FirebaseNotifications';
import Loading from './Loading';
import MembershipRequestApproval from './Mantine/Membership/MembershipRequestApproval';

// ---- Mantine bundle (loaded only for users on the "mantine" theme) ----
const MantineRoutes = lazy(() => import(/* webpackChunkName: "mantine-bundle" */ './Mantine/Routes'));

// ---- Semantic bundle  ----
const Login = lazy(() => import('./Login'));
const Reports = lazy(() => import('./Reports/Reports'));
const TicketForm = lazy(() => import('./Tickets/Forms/TicketForm'));
const ServiceRequest = lazy(() => import('./Tickets/Forms/ServiceRequest/ServiceRequest'));
const DGHome = lazy(() => import('./DG/DGHome'));
const CPUC = lazy(() => import('./CPUC/CPUC'));
const Tickets = lazy(() => import('./Tickets/Tickets'));
const CreateChangeRequest = lazy(() => import('./Tickets/Forms/CreateChangeRequest'));
const TCARequestForm = lazy(() => import('./Tickets/Forms/TCARMRequest/TCARequestForm'));
const AccessRequestForm = lazy(() => import('./Tickets/Forms/AccessRequest/AccessRequestForm'));
const RequestApproval = lazy(() => import('./Tickets/RequestApproval'));
const ReportsRedirect = lazy(() => import('./Reports/ReportsRedirect'));
const DGQuarterlyReports = lazy(() => import('./DG/DGQuarterlyReports'));
const DGInventory = lazy(() => import('./DG/DGInventory'));
const DGReport = lazy(() => import('./DG/DGReport'));
const ReportsDashboard = lazy(() => import('./Reports/ReportsDashboard'));
const Tasks = lazy(() => import('./Tasks/Tasks'));
const TicketDenied = lazy(() => import('./Tickets/Denied'));
const ReportsBuilderMainPage = lazy(() => import('./AdminComponents/ReportBuilder/ReportsBuilderMainPage'));
const PortalAdminMainPage = lazy(() => import('./AdminComponents/PortalAdmin/PortalAdminMainPage'));
const UserForm = lazy(() => import('./AdminComponents/PortalAdmin/Forms/UserForm/UserForm'));
const SiteAdminForm = lazy(() => import('./AdminComponents/PortalAdmin/Forms/SiteForm/SiteAdminForm'));
const NetworkMain = lazy(() => import('./AdminComponents/NetworkSheet/NetworkMain'));
const InternalTickets = lazy(() => import('./Tickets/InternalTickets'));
const EnergyIntegration = lazy(() => import('./EnergyIntegration/EnergyIntegration'));
const CIPEvidence = lazy(() => import('./CIPEvidence/CIPMain'));
const BulletinMainPage = lazy(() => import('./AdminComponents/Bulletin/BulletinMainPage'));
const InvoiceMain = lazy(() => import('./InvoiceGenerator/InvoiceMain'));
const NavBar = lazy(() => import('./NavBar/NavBar'));
const Emails = lazy(() => import('./AdminComponents/EmailAdmin/Emails'));
const TicketDetails = lazy(() => import('./Tickets/Details/TicketDetails'));
const Repository = lazy(() => import('./Repository/Repository'));
const Questionnaire = lazy(() => import('./Questionnaire/Questionnaire'));
const GridKnowledge = lazy(() => import('./GridKnowledge/GridKnowledge'));
const GridKnowledgeMain = lazy(() => import('./GridKnowledge/GridKnowledgeMain'));
const Portfolio = lazy(() => import('./Portfolio/Portfolio'));
const Kanban = lazy(() => import('./Kanban/Kanban'));
const Crowdstrike = lazy(() => import('./Crowdstrike/Crowdstrike'));
const Zoho = lazy(() => import('./Zoho/Zoho'));
const TicketMetrics = lazy(() => import('./TicketMetrics/TicketMetrics'));
const AlertsDashboard = lazy(() => import('./AdminComponents/AlertsDashboard/AlertsDashboard'));
const PublicRequest = lazy(() => import('./PublicRequest/PublicRequest'));
const PublicTasks = lazy(() => import('./Tasks/Public/PublicTasks'));
const CIPMediumTickets = lazy(() => import('./CIPMediumReporting/CIPMediumTickets'));
const Org = lazy(() => import('./Org/Org'));
const FeatureRequests = lazy(() => import('./FeatureRequests/FeatureRequests'));
const CIPPatchTrackerMain = lazy(() => import('./CIPPatchTracking/CIPPatchTrackerMain'));
const CIPAudit = lazy(() => import('./CIPAudit/CIPAudit'));
const CISA = lazy(() => import('./CISA/CISA'));
const SiteInfo = lazy(() => import('./Org/SiteInfo/SiteInfo'));
const AssetDetail = lazy(() => import('./Org/Assets/AssetDetail'));
const ProblemTicket = lazy(() => import('./Tickets/Forms/ProblemTicket/ProblemTicket'));
const OktaCallback = lazy(() => import('./OktaCallback'));

const RoutePaths = () => {

    const oktaAuth = useOktaAuth()
    const msAuth = useMsal()

    const setAdmin = (bool) => {
        localStorage.setItem('admin-storage', bool)
        updateDict('admin', bool)
    }

    const switch_theme = (theme) => {
        updateDict("theme", theme)
        api.put('/userprofile/update_theme', {}, {params: {theme: theme}})
    }

    const switch_scheme = (scheme) => {
        updateDict("scheme", scheme)
        api.put('/userprofile/update_scheme', {}, {params: {scheme: scheme}})
    }

    const updateDict = (key, value) =>{
        let updatedValue = {[key]: value}
        setUserContext(dict => ({
            ...dict,
            ...updatedValue
        }))
    }

    const [isLoading, setIsLoading] = useState(true)
    const [oktaAuthenticated, setOktaAuthenticated] = useState(null)
    const [msAuthenticated, setMSAuthenticated] = useState(null)
    const [userContext, setUserContext] = useState({
        userInfo: null,
        authVendor: null,
        headers: null,
        admin: false,
        token: "",
        visible: false,
        darkMode: false,
        theme: "semantic",
        scheme: "auto",
        setAdmin: setAdmin,
        switch_theme: switch_theme,
        setOktaAuth: setOktaAuth,
        setMSAuth: setMSAuth,
        switch_scheme: switch_scheme,
        updateDict:updateDict,
        msalInstance: null,
        impersonated: false
    })

    function setOktaAuth(){
        const token = oktaAuth.authState.accessToken.accessToken
        const data = qs.stringify({'username': 'okta', 'password':token})
        setIsLoading(true)
        axios({
            method: "POST",
            url: '/api/token',
            headers: {"Content-Type": "application/x-www-form-urlencoded"},
            data: data
        }).then(r => {
            axios.get('/api/me', {headers: {'Authorization': `Bearer ${token}`}}) // get userinfo
            .then(r => {
                let headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
                localStorage.setItem('bearer_token', `Bearer ${token}`)
                setOktaAuthenticated(true)
                setIsLoading(false)
                setUserContext({
                    headers: headers,
                    userInfo: r.data,
                    authVendor: 'okta',
                    hasReadNews: r.data.hasReadNews,
                    theme: r.data.theme,
                    scheme: r.data.scheme,
                    darkMode: r.data.darkMode,
                    token: token,
                    setAdmin: setAdmin,
                    switch_theme: switch_theme,
                    switch_scheme: switch_scheme,
                    updateDict:updateDict,
                })
            })
        }).catch(err => {
            setOktaAuthenticated(false)
            setIsLoading(false)
        })
    }

    // microsoft auth
    function setMSAuth(res){
        setIsLoading(true)
        let token = res.accessToken
        const data = qs.stringify({'username': 'microsoft', 'password': token})
        axios({
            method: "POST",
            url: '/api/token',
            headers: {"Content-Type": "application/x-www-form-urlencoded"},
            data: data
        }).then(r => {
            axios.get('/api/me', {headers: {'Authorization': `Bearer ${token}`}}) // get userinfo
            .then(r => {
                let headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
                localStorage.setItem('bearer_token', `Bearer ${token}`)
                localStorage.setItem('ms_token', JSON.stringify(res))
                setMSAuthenticated(true)
                setIsLoading(false)
                setUserContext({
                    headers: headers,
                    userInfo: r.data,
                    msAuthenticated: true,
                    authVendor: 'microsoft',
                    darkMode: r.data.darkMode,
                    theme: r.data.theme,
                    scheme: r.data.scheme,
                    token: token,
                    isAuthenticated: true,
                    setAdmin: setAdmin,
                    switch_theme: switch_theme,
                    switch_scheme: switch_scheme,
                    updateDict:updateDict,
                })
            }).catch(err => {
                setIsLoading(false)
                setMSAuthenticated(false)
            })
        }).catch(err => {
            setIsLoading(false)
            setMSAuthenticated(false)
        })
    }

    // okta auth
    useEffect(() => {
        let check_auth = false
        if(oktaAuth.authState && userContext.userInfo === null){
            if(oktaAuth.authState.isAuthenticated){
                if(oktaAuth.authState.accessToken.accessToken && userContext.userInfo === null){
                    check_auth = true
                }
                if(userContext.userInfo){
                    if(oktaAuth.authState.accessToken.accessToken !== userContext.userInfo.portal_token){
                        check_auth = true
                    }
                }
            }
        }
        if(check_auth){
            setOktaAuth()
        }
    }, [oktaAuth])

    // microsoft auth
    useEffect(() => {
        if(msAuth.accounts.length > 0 && msAuth.inProgress === "none" && userContext.userInfo === null){
            const request = {
                scopes: ["user.read"],
                account: msAuth.accounts[0]
            };
            async function get_token(){
                msAuth.instance.acquireTokenSilent(request).then(res => {
                    setMSAuth(res)
                }).catch(err => {
                    setIsLoading(false)
                    setMSAuthenticated(false)
                })
            }
            let ms = JSON.parse(localStorage.getItem("ms_token"))
            if(ms){
                if(new Date(ms.expiresOn) > new Date() || msAuthenticated === null){
                    get_token()
                }
            } else {
                get_token()
            }
            
        }
    }, [msAuth.inProgress])

    // check if authenticated keep loading screen on
    useEffect(() => {
        let authenticated = false
        if(msAuth.accounts.length > 0){
            authenticated = true
        }
        if(oktaAuth.authState){
            if(oktaAuth.authState.isAuthenticated){
                authenticated = true
            }
        }
        if(authenticated === false){
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        if(userContext.userInfo){
            if(userContext.userInfo.teams.includes("GridSec")){
                const baseURL = window.location.origin
                const urls = ["https://portal.gridsec.com"]
                if(urls.includes(baseURL)){
                    let new_url = "https://gridsecurity.io"
                    window.location.replace(new_url)
                    window.location.assign(new_url)
                    window.location.href = new_url
                }
            }
        }
    }, [userContext])

    //  if(isLoading){
    //     return <Loading />
    // }

    if(userContext.userInfo){
        if(userContext.theme === "mantine"){
            return <UserContext.Provider value={userContext}>
                <Suspense fallback={<Loading />}>
                    <MantineRoutes />
                </Suspense>
            </UserContext.Provider>
        }
    }

    return (
        <UserContext.Provider value={userContext}>
            <ThemeProvider theme={userContext.darkMode === false ? lightTheme : darkTheme}>
            <GlobalStyle/>
            <div className='sidebar-article' style={{ marginLeft: userContext.userInfo===null ?'0 !important':'210px'}}>
                {userContext.userInfo ? (
                    <Suspense fallback={null}>
                        <NavBar setUserContext={updateDict} />
                    </Suspense>
                ) : null}
                <div>
                    <Suspense fallback={<Loading />}>
                        <Routes>
                            <Route path="/" element={<Login loading={isLoading} oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated}/>} exact />
                            <Route path="/requests/approval" element={<RequestApproval />} />
                            <Route path="/membership/approval" element={<><Notifications /><MembershipRequestApproval /></>} />
                            <Route path="/public/request" element={<PublicRequest />} />
                            <Route path="/tasks/public" element={<PublicTasks />} />
                            <Route path="/reports" exact>
                                <Route index element={<SecureRoute Route oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><ReportsDashboard /></SecureRoute>} />
                                <Route path=":site" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><ReportsRedirect /></SecureRoute>} exact />
                                <Route path=":site/:date" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><Reports /></SecureRoute>} exact />
                            </Route>
                            <Route path="/tickets/incident" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading}><TicketForm /></SecureRoute>} exact />
                            <Route path="/tickets/service" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading}><ServiceRequest /></SecureRoute>} exact />
                            <Route path="/tickets/problem" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading}><ProblemTicket /></SecureRoute>} exact />
                            <Route path="/tickets/denied" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><TicketDenied /></SecureRoute>} exact />
                            <Route path="/tickets/details/:number" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><TicketDetails /></SecureRoute>} exact /> 
                            <Route path="/tickets" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading}><Tickets /></SecureRoute>} exact />
                            <Route path="/tickets/access" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading}><AccessRequestForm /></SecureRoute>} exact />
                            <Route path="/tickets/tca" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><TCARequestForm /></SecureRoute>} exact />
                            <Route path="/tickets/change" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading}><CreateChangeRequest /></SecureRoute>} exact />
                            <Route path="/dg" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><DGHome /></SecureRoute>} exact />
                            <Route path="/dg/select" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><DGQuarterlyReports /></SecureRoute>} exact />
                            <Route path="/dg/inventory" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading}><DGInventory /></SecureRoute>} exact />
                            <Route path="/dg/report/:date" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><DGReport /></SecureRoute>} exact />
                            <Route path="/cpuc" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><CPUC /></SecureRoute>} exact />
                            <Route path="/energyintegration" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><EnergyIntegration /></SecureRoute>} exact />
                            <Route path="/invoice" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><InvoiceMain /></SecureRoute>} exact />
                            {/* <Route path="/contacts" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><Contacts /></SecureRoute>} exact /> */}
                            <Route path="/repository" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><Repository /></SecureRoute>} exact />
                            <Route path="/gridknowledge" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><GridKnowledgeMain /></SecureRoute>} exact />
                            <Route path="/gridknowledge/:id" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><GridKnowledge /></SecureRoute>} exact />
                            {/* <Route path="/questionnaire/:id" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><QuestionnaireDetail /></SecureRoute>} exact /> */}
                            <Route path="/portfolio" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><Portfolio /></SecureRoute>} exact />
                            <Route path="/organization" exact>
                                <Route index element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><Org /></SecureRoute>} />
                                <Route path=":org" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><Org /></SecureRoute>} />
                                <Route path=":org/:site" element={<SiteInfo />} />
                                <Route path=":org/:site/asset/:asset" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><AssetDetail /></SecureRoute>} />
                            </Route>
                            <Route path="/featurerequests" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><FeatureRequests /></SecureRoute>} exact/>
                            <Route path='/ciptracker' exact>
                                <Route index element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><CIPPatchTrackerMain/></SecureRoute>} exact/>
                                <Route path=":site" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><CIPPatchTrackerMain/></SecureRoute>} exact/>
                                <Route path=":site/tracker" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><CIPPatchTrackerMain/></SecureRoute>} exact />
                                <Route path=":site/report/" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><CIPPatchTrackerMain/></SecureRoute>} exact />
                                <Route path=":site/report/:reportId" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><CIPPatchTrackerMain/></SecureRoute>} exact />
                                <Route path=":site/mitigation" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><CIPPatchTrackerMain/></SecureRoute>} exact />
                                <Route path=":site/mitigation/:planId" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><CIPPatchTrackerMain/></SecureRoute>} exact />

                            </Route>
                            <Route path="/cipaccessauth" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><CIPAudit/></SecureRoute>} exact/>
                            
                            {/* Admin routes */}
                            <Route path="/admin/tasks" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><Tasks /></SecureRoute>} exact />
                            <Route path="/admin/reportbuilder" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><ReportsBuilderMainPage /></SecureRoute>} exact />
                            <Route path="/admin/portaladmin" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><PortalAdminMainPage /></SecureRoute>} exact />
                            <Route path="/admin/portaladmin/user/details/:id" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><UserForm /></SecureRoute>} exact />
                            <Route path="/admin/portaladmin/siteadmin/details/:id" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><SiteAdminForm /></SecureRoute>} exact />
                            <Route path="/admin/network" element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><NetworkMain /></SecureRoute>} exact />
                            <Route path='/admin/tickets' element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><InternalTickets /></SecureRoute>} exact />
                            <Route path='/admin/cipevidence' element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><CIPEvidence /></SecureRoute>} exact />
                            <Route path='/admin/bulletin' element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><BulletinMainPage /></SecureRoute>} exact />
                            <Route path='/admin/email' element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><Emails /></SecureRoute>} exact />
                            <Route path='/admin/alerts' element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><AlertsDashboard /></SecureRoute>} exact />
                            <Route path='/admin/questionnaire' element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><Questionnaire /></SecureRoute>} exact />
                            <Route path='/kanban' element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><Kanban /></SecureRoute>} exact />
                            <Route path='/crowdstrike' element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><Crowdstrike /></SecureRoute>} exact />
                            <Route path='/zoho' element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading}><Zoho /></SecureRoute>} exact />
                            <Route path='/metrics' element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><TicketMetrics /></SecureRoute>} exact />
                            <Route path='/cip_medium_reports' element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><CIPMediumTickets /></SecureRoute>} exact />
                            <Route path='/cisa' element={<SecureRoute oktaAuthenticated={oktaAuthenticated} msAuthenticated={msAuthenticated} loading={isLoading} ><CISA /></SecureRoute>} exact />
                            <Route path='/login/okta/callback' element={<OktaCallback />} />
                        </Routes>
                    </Suspense>
                </div>
            </div>
            </ThemeProvider>
        </UserContext.Provider>
    )
}

export default RoutePaths